/**
 * The client's copy of the one proof shape (trust audit Phase 2, "proof
 * chips"). The server's `src/server/services/proof.ts` is the source; a
 * server test (`proof.test.ts`) reads this file and fails the build if the
 * labels, symbols or words drift.
 *
 *   FACT      ✓  Proven      we hold the record
 *   ESTIMATE  ~  Best guess  GWC worked it out or copied it
 *   MISSING   ?  Missing     nothing on file
 *
 * Where a chip shows (Corbin 10/28):
 *   - busy screens (lists, Closeout cards, the Grid): a proven fact is only a
 *     small gray ✓ (green on hover, the sheet on tap);
 *   - the record's own page (device, invoice): the full green chip;
 *   - Best guess and Missing always show their words, everywhere;
 *   - never on anything the customer sees.
 *
 * The chips are ON for everyone (Corbin 10/28: "the proof chips don't hurt
 * anything so don't leave it off"). The per-person switch, `trust.proof-chips`
 * (same server-backed store as "Try the new Grid"), stays as an opt-out:
 * anyone can turn the chips off on their own screen. Nothing saved = on.
 */
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { useUserPreference } from "@/lib/user-preference";

export const PROOF_LABELS = ["FACT", "ESTIMATE", "MISSING"] as const;
export type ProofLabel = (typeof PROOF_LABELS)[number];

export const PROOF_SYMBOL: Record<ProofLabel, string> = {
    FACT: "✓",
    ESTIMATE: "~",
    MISSING: "?",
};

export const PROOF_WORD: Record<ProofLabel, string> = {
    FACT: "Proven",
    ESTIMATE: "Best guess",
    MISSING: "Missing",
};

export interface ProofFix {
    label: string;
    /** An in-app path to go to. */
    href: string | null;
    /** A verb the screen knows how to do ("edit_due_date", "ask_tech"). */
    action: string | null;
}

export interface Proof {
    label: ProofLabel;
    words: string;
    title: string;
    source: string | null;
    date: string | null;
    who: string | null;
    why: string | null;
    open: { label: string; href: string } | null;
    fix: ProofFix | null;
}

/** "busy" = lists, cards, the Grid; "page" = the record's own page. */
export type ProofPlace = "busy" | "page";

/** A proven fact on a busy screen shrinks to the small gray ✓. */
export function isQuiet(proof: Pick<Proof, "label">, place: ProofPlace): boolean {
    return proof.label === "FACT" && place === "busy";
}

/** What a screen reader says for a chip: "Proven: from the report 10/17/25". */
export function proofAriaLabel(proof: Pick<Proof, "label" | "words">): string {
    const words = proof.words.replace(/^(Best guess|Missing):\s*/, "");
    return `${PROOF_WORD[proof.label]}: ${words.charAt(0).toLowerCase()}${words.slice(1)}`;
}

export const PROOF_CHIPS_PREF_KEY = "trust.proof-chips";

/** What a person who never touched the switch sees: chips on (Corbin 10/28). */
export const PROOF_CHIPS_DEFAULT_ON = true;

/**
 * The per-person switch on office pages (the same people who can manage
 * Office Review, like "Try the new job page"). ON unless the person turned it
 * off for themselves. A failed save says so and rolls back.
 */
export function useProofChipsSwitch(): { allowed: boolean; on: boolean; set: (on: boolean) => void; loaded: boolean } {
    const { can } = useAuth();
    const allowed = can(PERMISSIONS.officeReview.manage);
    const [value, set, { loaded }] = useUserPreference<boolean>(PROOF_CHIPS_PREF_KEY, PROOF_CHIPS_DEFAULT_ON, {
        enabled: allowed,
        onError: () => toast.error("Couldn't save the how-sure switch. It's back the way it was."),
    });
    // Wait for the saved answer before drawing or fetching anything, so someone
    // who turned the chips off never sees them flash on (or costs a proof read).
    return { allowed, on: allowed && loaded && value !== false, set, loaded };
}
